/**
 * Composants partagés du système de design (direction C) — docs/design/systeme-de-design.md.
 * Ils affichent ; ils ne calculent rien : prix et dates passent par utils/currencyUtils.js, statuts par
 * utils/statusLabels.js. Bouton : components/ui/button.jsx (variante « payment » pour l'argent de l'expéditeur).
 */
import React from 'react';
import { statusInfo } from '@/utils/statusLabels';
import { formatAmount, frDate } from '@/utils/currencyUtils';
import '@/styles/design-components.css';

/** Pastille de statut : libellé français et couleur venus du dictionnaire unique, jamais le code brut. */
export function StatusPill({ domain, code, className = '', ...rest }) {
  const { label, tone } = statusInfo(domain, code);
  return (
    <span className={`c-pill c-pill--${tone} ${className}`.trim()} data-status={code || ''} {...rest}>
      {label}
    </span>
  );
}

/** État vide : icône centrée, titre, texte, action facultative. `icon` est un composant Lucide. */
export function EmptyState({ icon: Icon, title, children, action, ...rest }) {
  return (
    <div className="c-empty" {...rest}>
      {Icon && <span className="c-empty-icon"><Icon size={28} strokeWidth={1.8} aria-hidden="true" /></span>}
      <h2 className="c-empty-title">{title}</h2>
      {children && <p className="c-empty-text">{children}</p>}
      {action && <div className="c-empty-action">{action}</div>}
    </div>
  );
}

/** En-tête de page : un seul titre, un sous-titre, les actions à droite (qui passent dessous sur téléphone). */
export function PageHeader({ title, subtitle, actions }) {
  return (
    <header className="c-page-header">
      <div>
        <h1 className="c-page-header-title">{title}</h1>
        {subtitle && <p className="c-page-header-sub">{subtitle}</p>}
      </div>
      {actions && <div className="c-page-header-actions">{actions}</div>}
    </header>
  );
}

const TONS_AVATAR = ['marine', 'safran', 'vert'];

/** Avatar à initiales ; la couleur dépend du nom, pour qu'une même personne garde la même. */
export function MemberAvatar({ firstName = '', lastName = '', size = 40 }) {
  const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || '?';
  const seed = [...`${firstName}${lastName}`].reduce((n, ch) => n + ch.charCodeAt(0), 0);
  const tone = TONS_AVATAR[seed % TONS_AVATAR.length];
  return (
    <span className={`c-avatar c-avatar--${tone}`} style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }} aria-hidden="true">
      {initials}
    </span>
  );
}

/** Montant dans sa devise, au format français (« 1 250,5 € », « 22 000 FCFA »), avec une unité facultative. */
export function Price({ amount, currency, unit }) {
  return (
    <span className="c-price">
      {formatAmount(amount, currency)}
      {unit && <span className="c-price-unit"> {unit}</span>}
    </span>
  );
}

/**
 * Date au format JJ/MM/AAAA. Une date seule (« 2026-11-12 ») est lue telle quelle (frDate) ;
 * une date avec heure passe par le fuseau du navigateur.
 */
export function DateLabel({ value }) {
  if (!value) return null;
  const text = /^\d{4}-\d{2}-\d{2}$/.test(value) ? frDate(value) : new Date(value).toLocaleDateString('fr-FR');
  return <span className="c-date">{text}</span>;
}
